import * as React from 'react';
import { useCallback, useState } from 'react';

import useWidth from '@components/Hooks/useWidth';
import { useOnKeyChange } from '../../../hooks/useOnKeyChange';

export const MassActionsContext = React.createContext({} as MassActionsContextProps);

export const MassActionsContextProvider = ({
  children,
  data,
  fieldRef,
}: MassActionsContextProviderProps) => {
  const {
    viewMode,
    list: { selecteds, changeSelecteds, count },
    listOrDefault,
    listActions: { toggleAllSelected },
    isAllItemsSelected,
    selectionKey,
  } = data;

  const { mobileWidth } = useWidth();

  const [massActionFilter, setSelectByFilter] = useState(false);
  const [massActionMobile, setMobileSelection] = useState(false);

  useOnKeyChange(selectionKey, () => setSelectByFilter(false));

  const toggle = useCallback(
    () => toggleAllSelected(isAllItemsSelected, listOrDefault, selecteds, fieldRef),
    [isAllItemsSelected, listOrDefault, selecteds, toggleAllSelected, fieldRef],
  );

  const isMobileGrid = mobileWidth && viewMode?.active === 'GRID';

  const shouldMobileToggle = !massActionMobile && selecteds.length === 0 && isMobileGrid;

  const toggleMassActionFilter = () => {
    setSelectByFilter(!massActionFilter);
    return changeSelecteds([]);
  };

  const clearSelection = () => {
    setSelectByFilter(false);
    setMobileSelection(false);
    changeSelecteds([]);
  };

  const toggleListItems = () => {
    if (massActionFilter) {
      return toggleMassActionFilter();
    }

    if (!isMobileGrid) {
      return toggle();
    }

    if (selecteds.length !== 0) {
      changeSelecteds([]);
      return setMobileSelection(false);
    }

    if (shouldMobileToggle) {
      return setMobileSelection(true);
    }

    if (isAllItemsSelected) {
      setMobileSelection(false);
    }

    return toggle();
  };

  return (
    <MassActionsContext.Provider
      value={{
        massActionMobile,
        massActionFilter,
        toggleListItems,
        toggleMassActionFilter,
        clearSelection,
        filterMaxResults: count,
      }}
    >
      {children}
    </MassActionsContext.Provider>
  );
};

export const useMassActionsContext = () => React.useContext(MassActionsContext);

type MassActionsContextProps = {
  massActionMobile: boolean;
  massActionFilter: boolean;
  toggleListItems: () => void;
  toggleMassActionFilter: () => void;
  clearSelection: () => void;
  filterMaxResults?: number;
};

export type MassActionData<T = any> = {
  listOrDefault: T[];
  isAllItemsSelected: boolean;
  selectionKey: string;
  viewMode?: { active?: 'GRID' | 'LIST' };
  list: {
    selecteds: string[];
    changeSelecteds: (items: string[]) => void;
    count?: number;
  };
  listActions: {
    toggleAllSelected: (
      isAllSelected: boolean,
      list: T[],
      selecteds: string[],
      fieldRef?: string,
    ) => void;
  };
};

type MassActionsContextProviderProps = {
  data: MassActionData;
  fieldRef?: string;
  children?: React.ReactNode;
};
